import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import MUIProvider from '~/contexts/MUIProvider';

import { Alert, Button, Container, Stack, TextField } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import useAppNavigation from '~/hooks/useAppNavigation';

import DealCrawlerService from '~/services/cruises/DealCrawlerService';

export default function DealCrawlerCreate() {
  const [sourcePageUrl, setSourcePageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const { push } = useAppNavigation();

  const handleStartCrawler = useCallback(async () => {
    if (isLoading || !sourcePageUrl) {
      return;
    }

    setIsLoading(true);

    try {
      const dealCrawler = await DealCrawlerService.crawlDealPage(sourcePageUrl);

      enqueueSnackbar('Deal page crawler started successfully', {
        variant: 'success',
      });

      setSourcePageUrl(null);
      push(`/cruises/deal-crawler/${dealCrawler.id}/view`);
    } catch (error) {
      enqueueSnackbar(`Error crawling deal page: ${error.message}`, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [sourcePageUrl, isLoading, enqueueSnackbar, push]);

  return (
    <MUIProvider>
      <Helmet>
        <title>Start crawler for a deal page</title>
      </Helmet>

      <Container maxWidth="xl">
        <PageHeader title="Start crawler for a deal page" backButton />

        <Stack direction="column" spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              required
              fullWidth
              name="sourcePageUrl"
              label="Source Page URL"
              onChange={(e) => setSourcePageUrl(e.target.value)}
              value={sourcePageUrl}
            />

            <Button variant="contained" color="primary" onClick={handleStartCrawler}>
              {isLoading ? <Spinner size={20} /> : 'Start crawler'}
            </Button>
          </Stack>

          {isLoading && (
            <Alert severity="info">
              It may take a few seconds. You will be redirected to the crawler page when it's done.
            </Alert>
          )}
        </Stack>
      </Container>
    </MUIProvider>
  );
}
